import * as React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import { useMappedState } from "redux-react-hook";

import {
  IOrder,
  ListOrdersByDropParams,
  ordersApi,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import { useDrop } from "src/core/drops/hooks";
import { PaginatedData } from "src/library/utils";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text/Text";
import { IStore } from "src/core/shared/store";

import UpcomingPaymentRow from "./UpcomingPaymentRow";

const UpcomingPaymentsDashboardWidget: React.ReactFCWithChildren = () => {
  const [ getOrdersRes, getOrdersReq ] = ordersApi<{ orders: PaginatedData<IOrder> }>("LIST_DROP_ORDERS");
  const [ { drop } ] = useDrop();
  const orderStatuses = useMappedState((store: IStore) => store.global.settings?.orderStatuses);

  React.useEffect(() => {
    if (drop && orderStatuses) {
      const provisionalStatuses = orderStatuses.filter(os => os.settings.group === "PROVISIONAL");

      getOrdersReq<ListOrdersByDropParams>({
        dropId: drop.id,
        pagination: {
          offset: 0,
          page: 0,
          sort: [ [ "orderDeliveryDate", "ASC" ] ],
          limit: 10
        },
        filters: {
          orderStatusId: _.map(provisionalStatuses, "id"),
          orderDeliveryDate: { $gte: moment.utc().startOf("day").format("YYYY-MM-DD") },
          paymentStatus: {
            $or: [
              {
                $notIn: [
                  OrderPaymentStatus.CAPTURED,
                  OrderPaymentStatus.PARTIAL_REFUNDED,
                  OrderPaymentStatus.REFUNDED,
                  OrderPaymentStatus.REFUND_FAILED,
                  OrderPaymentStatus.UNCAPTURED
                ]
              },
              { $is: null }
            ]
          }
        }
      });
    }
  }, [ drop, orderStatuses ]);

  return (
    <UpcomingPaymentsDashboardWidgetWrapper>
      {getOrdersRes.pending ? (
        <LoadingSpinner
          type="circular-pulse"
          size={30}
        />
      ) : (
        <div className="payments-list">
          {getOrdersRes.data?.orders.result.length ? _.map(getOrdersRes.data?.orders.result, order => (
            <UpcomingPaymentRow order={order} />
          )) : (
            <Text>
              You have no recent payments
            </Text>
          )}
        </div>
      )}
    </UpcomingPaymentsDashboardWidgetWrapper>
  );
};

export default UpcomingPaymentsDashboardWidget;

const UpcomingPaymentsDashboardWidgetWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    background: ${theme.colors.white};

    .payments-list {
      overflow-y: scroll;
      height: 100%;
    }

    .unpaid-order {
      width: 100%;
      padding: ${theme.spacing.padding.sm};
      border: 2px solid ${theme.colors.lightGrey};
      margin-bottom: ${theme.spacing.margins.sm};
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      
      .order-info {
        display: flex;
        flex-direction: row;
      }

      .info-row {
        display: flex;
        flex-direction: column;
        margin-right: ${theme.spacing.margins.sm};
      }
    }
  `}
`;
